// F3c-c1 GREEN — BusinessAddressPanel. Inline StructuredAddressSection HQ-address
// editor, ported from deprecated-business@16fd6b71.
//
//   - Renders StructuredAddressSection.
//   - Autosaves ONLY a VERIFIED address (lat/lng present) via updateOrg — the
//     PATCH fires from onAddressVerified the moment a Google match attaches
//     coordinates.
//   - An unverified / partial address is rejected with "Verify the headquarters
//     address before saving." on submit.
//   - The whole editor LOCKS after approval (status ∈ {approved, active,
//     suspended}); no PATCH fires while locked.
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";
import type { AddressResult } from "@bw/shared/components";

import { updateOrg, type Org, type OrgUpdate } from "@/libs/onboardingApi";
import {
  StructuredAddressSection,
  addressDraftToAddress,
  createAddressDraft,
  hasAnyAddressInput,
  isAddressDraftVerified,
  type AddressDraft,
} from "@/components/address/StructuredAddressSection";
import { FormMessage } from "./shared";

const LOCKED_ORG_STATUSES = new Set<string>([
  "approved",
  "active",
  "suspended",
]);

const visuallyHidden = {
  position: "absolute",
  width: "1px",
  height: "1px",
  p: 0,
  m: -1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
} as const;

// Translate the verified shared Address into the org-update address body. The
// PATCH body uses snake_case + latitude/longitude (the backend's Stripe-aligned
// Address shape) which is exactly what addressDraftToAddress emits.
function draftToOrgAddress(draft: AddressDraft): OrgUpdate["address"] {
  const address = addressDraftToAddress(draft);
  if (!address) return undefined;
  return {
    line1: address.line1,
    line2: address.line2,
    city: address.city,
    state: address.state,
    postal_code: address.postal_code,
    country: address.country,
    latitude: address.latitude,
    longitude: address.longitude,
  };
}

export function BusinessAddressPanel({
  org,
  orgId,
}: {
  org: Org;
  orgId: string;
}) {
  const queryClient = useQueryClient();
  const [addressDraft, setAddressDraft] = useState(() =>
    createAddressDraft(org.address),
  );
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const locked = LOCKED_ORG_STATUSES.has(org.status);

  useEffect(() => {
    setAddressDraft(createAddressDraft(org.address));
  }, [org.address]);

  const mutation = useMutation({
    mutationFn: (draft: AddressDraft) => {
      const address = draftToOrgAddress(draft);
      if (!address) {
        throw new Error("Verify the headquarters address before saving.");
      }
      return updateOrg(orgId, { address });
    },
    onSuccess: () => {
      setMessage("Saved.");
      setError("");
      void queryClient.invalidateQueries({ queryKey: ["org", "me"] });
      void queryClient.invalidateQueries({
        queryKey: ["org", orgId, "onboarding-state"],
      });
    },
    onError: (err: Error) => {
      setMessage("");
      setError(err.message || "Could not save headquarters address.");
    },
  });

  function buildVerifiedDraft(result: AddressResult): AddressDraft {
    return createAddressDraft(
      {
        line1: result.address_line1,
        line2: addressDraft.line2,
        city: result.city,
        state: result.state,
        postal_code: result.postal_code,
        country: result.country,
        latitude: result.lat,
        longitude: result.lng,
      },
      result.formatted_address,
    );
  }

  // A Google match attached coordinates — autosave the verified address.
  function handleAddressVerified(result: AddressResult) {
    if (locked || mutation.isPending) return;
    setMessage("");
    setError("");
    mutation.mutate(buildVerifiedDraft(result));
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setMessage("");
    setError("");
    if (locked) return;
    if (
      !hasAnyAddressInput(addressDraft) ||
      !isAddressDraftVerified(addressDraft)
    ) {
      setError("Verify the headquarters address before saving.");
      return;
    }
    mutation.mutate(addressDraft);
  }

  return (
    <Stack component="form" onSubmit={handleSubmit} spacing={2}>
      <StructuredAddressSection
        value={addressDraft}
        onChange={setAddressDraft}
        idPrefix="getting-started-business-address"
        autocompleteLabel="Headquarters Address"
        autocompletePlaceholder="Search the business address"
        manualLine1Label="Headquarters Address Line 1"
        helperText="This is the business or legal mailing address used for platform review."
        onAddressVerified={handleAddressVerified}
        disabled={locked || mutation.isPending}
      />
      {/* An explicit submit so pressing Enter in a structured field runs the
          verify-guard (the form has multiple inputs and no other submit control;
          the happy path autosaves from onAddressVerified). */}
      <Button type="submit" sx={visuallyHidden} disabled={locked}>
        Save headquarters address
      </Button>
      {locked ? (
        <FormMessage tone="error">
          Headquarters address is locked after platform approval.
        </FormMessage>
      ) : null}
      {mutation.isPending ? (
        <Typography color="text.secondary" role="status" variant="caption">
          Saving…
        </Typography>
      ) : null}
      {message ? <FormMessage tone="success">{message}</FormMessage> : null}
      {error ? <FormMessage tone="error">{error}</FormMessage> : null}
    </Stack>
  );
}
